import React, { useState } from 'react';
import { useNavigation, NavigationProp, useRoute } from '@react-navigation/native';
import { AuthStackParamList } from '../../navigation/AuthNavigator';
import { auth, signInWithEmailAndPassword } from '../../config/firebase';
import { useNavigationLogging } from '../../hooks/useNavigationLogging';
import { completeOnboarding as markOnboardingComplete } from '../../utils/onboardingStorage';
import { signInWithApple, signInWithGoogle } from '../../services/authService';
import { showAlert } from '../../utils/alert';
import {
  FirebaseProfile,
  fetchOldProfileData,
  hasRecommendationsForUser,
  handleSocialSignIn,
  migrateAnonymousProfile,
} from './authShared';
import AuthTemplate from './AuthTemplate';

interface LoginScreenParams {
  fromOnboarding?: boolean;
}

const LoginScreen: React.FC = () => {
  useNavigationLogging('LoginScreen');
  
  const navigation = useNavigation<NavigationProp<AuthStackParamList>>();
  const route = useRoute();
  
  const params = route.params as LoginScreenParams | undefined;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [hasInteractedWithEmail, setHasInteractedWithEmail] = useState(false);
  const [hasInteractedWithPassword, setHasInteractedWithPassword] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const loadingTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  const validateEmail = (email: string) => {
    if (!email.trim()) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validatePassword = (password: string) => {
    if (!password.trim()) {
      return 'Password is required';
    }
    return '';
  };

  const handleEmailChange = (text: string) => {
    setEmail(text);
    if (hasInteractedWithEmail) {
      setEmailError(validateEmail(text));
    }
  };

  const handlePasswordChange = (text: string) => {
    setPassword(text);
    if (hasInteractedWithPassword) {
      setPasswordError(validatePassword(text));
    }
  };

  const handleEmailBlur = () => {
    setHasInteractedWithEmail(true);
    setEmailError(validateEmail(email));
  };

  const handlePasswordBlur = () => {
    setHasInteractedWithPassword(true);
    setPasswordError(validatePassword(password));
  };

  const isFormValid = () => {
    return validateEmail(email) === '' && validatePassword(password) === '';
  };

  const handleLogin = async () => {
    // Validate all fields and show errors
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);
    
    setEmailError(emailValidation);
    setPasswordError(passwordValidation);
    setHasInteractedWithEmail(true);
    setHasInteractedWithPassword(true);
    
    if (!isFormValid()) {
      return;
    }

    setLoading(true);
    try {
      // Capture anonymous user ID and data before sign-in
      const previousUserId = auth.currentUser?.uid;
      const wasAnonymous = auth.currentUser?.isAnonymous;
      const oldProfileData =
        previousUserId && wasAnonymous ? await fetchOldProfileData(previousUserId) : null;

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userId = userCredential.user.uid;
      
      // If user was anonymous and signed in with existing account, migrate data
      if (previousUserId && wasAnonymous && previousUserId !== userId) {
        await migrateAnonymousProfile(previousUserId, userId, oldProfileData);
      }
      
      // Check if user has actual profile data with recommendations
      const hasRecommendations = await hasRecommendationsForUser(userId);
      await navigateAfterLogin(hasRecommendations);
      
      setLoading(false);
    } catch (error: any) {
      console.error('Login error:', error);
      
      // Helper to format error codes for user display
      const formatErrorCode = (code: string | undefined): string => {
        if (!code) return 'Unknown Error';
        return code.replace('auth/', '').split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
      };
      
      // Extract actual error code from wrapped message or use direct code
      let actualErrorCode = error.code;
      const wrappedMessage = error?.customData?.message;
      if (wrappedMessage && typeof wrappedMessage === 'string') {
        const match = wrappedMessage.match(/auth\/[\w-]+/);
        if (match) {
          actualErrorCode = match[0];
        }
      }
      
      // Map common errors to friendly messages
      let errorMessage: string;
      switch (actualErrorCode) {
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          errorMessage = 'Invalid password. Please try again.';
          break;
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address.';
          break;
        case 'auth/invalid-email':
        errorMessage = 'That email address is invalid!';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled.';
          break;
        case 'auth/too-many-requests':
        errorMessage = 'Too many login attempts. Please try again later.';
          break;
        default:
          // For any other error, show the formatted error code
          errorMessage = formatErrorCode(actualErrorCode);
      }
      
      showAlert('Login Failed', errorMessage);
      setLoading(false);
    }
  };

  const navigateToRegister = () => {
    // Pass along the fromOnboarding flag if present
    if (params?.fromOnboarding) {
      (navigation as any).navigate('Register', { fromOnboarding: true });
    } else {
      navigation.navigate('Register');
    }
  };

  const navigateAfterLogin = async (hasRecommendations: boolean) => {
    if (hasRecommendations || params?.fromOnboarding) {
      if (params?.fromOnboarding) {
        await markOnboardingComplete();
      }
        (navigation as any).reset({
          index: 0,
          routes: [{ name: 'Main' }],
        });
    } else {
      (navigation as any).reset({
        index: 0,
        routes: [{ name: 'DiscoveryV2Onboarding' }],
      });
    }
  };

  const navigateToForgotPassword = () => {
    navigation.navigate('ForgotPassword');
  };

  const handleAppleLogin = async () =>
    handleSocialSignIn({
      signIn: signInWithApple,
      providerLabel: 'Apple',
      loading: appleLoading,
      setLoading: setAppleLoading,
      onNavigateAfterAuth: navigateAfterLogin,
    });

  const handleGoogleLogin = async () =>
    handleSocialSignIn({
      signIn: signInWithGoogle,
      providerLabel: 'Google',
      loading: googleLoading,
      setLoading: setGoogleLoading,
      onNavigateAfterAuth: navigateAfterLogin,
    });

  return (
    <AuthTemplate
      mode="login"
      title="Welcome Back"
      email={email}
      password={password}
      emailError={emailError}
      passwordError={passwordError}
      onEmailChange={handleEmailChange}
      onPasswordChange={handlePasswordChange}
      onEmailBlur={handleEmailBlur}
      onPasswordBlur={handlePasswordBlur}
      onEmailPasswordSubmit={handleLogin}
      emailPasswordLoading={loading}
      onApplePress={handleAppleLogin}
      appleLoading={appleLoading}
      onGooglePress={handleGoogleLogin}
      googleLoading={googleLoading}
      onForgotPassword={navigateToForgotPassword}
      onSecondaryAction={navigateToRegister}
      secondaryActionLabel="Create Account"
    />
  );
};

export default LoginScreen; 